import React, { useEffect, useState, useContext } from "react";
import { CombinedContext } from "../../../context/combinedContext";
import translations from "../../../translations/translations";
import companyBasedRequest from "../../../request/companyBasedRequest";
import "./teamServicesModal.css";
import { Home, Monitor, Check } from "lucide-react";

const TeamServicesModal = ({ isOpen, onClose, shopServices = [], staffProfile = null, onSave }) => {
	const { language, userData } = useContext(CombinedContext);
	const [rows, setRows] = useState([]);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!isOpen || !staffProfile) return;
		if (!shopServices || shopServices.length === 0) return;

		const staffServiceIds = staffProfile.services.map((s) => s.serviceId || s);
		const staffSet = new Set(staffServiceIds);

		const mapped = shopServices.map((svc) => ({
			serviceId: svc.serviceId,
			name: svc.name,
			price: svc.price,
			currency: svc.currency,
			duration: svc.duration,
			isHomeService: svc.isHomeService,
			locationType: svc.locationType || (svc.isHomeService ? "home" : "in_shop"),
			active: staffSet.has(svc.serviceId),
		}));

		setRows(mapped);
	}, [isOpen, shopServices, staffProfile]);

	useEffect(() => {
		if (!isOpen) setRows([]);
	}, [isOpen]);

	const toggleRow = (index) => {
		setRows((prev) => {
			const next = [...prev];
			next[index] = { ...next[index], active: !next[index].active };
			return next;
		});
	};

	const selectedCount = rows.filter((r) => r.active).length;

	const handleSave = async () => {
		const serviceIds = rows.filter((r) => r.active).map((r) => r.serviceId);
		try {
			setSaving(true);
			await companyBasedRequest.updateUserServices(
				staffProfile.staffId,
				serviceIds,
				userData.cognitoTokens.AccessToken
			);
			onSave?.(serviceIds);
			onClose(); // close AFTER success
		} catch (err) {
			console.error("Failed to save staff services", err);
		} finally {
			setSaving(false);
		}
	};

	if (!isOpen || !staffProfile) return null;

	const t = translations[language];

	const locationBadge = (row) => {
		if (row.locationType === "home") {
			return (
				<span className="sf-tsm-badge sf-tsm-badge--home">
					<Home size={12} strokeWidth={1.9} />
					{language === "es" ? "A domicilio" : "Home service"}
				</span>
			);
		}
		if (row.locationType === "online") {
			return (
				<span className="sf-tsm-badge sf-tsm-badge--online">
					<Monitor size={12} strokeWidth={1.9} />
					{language === "es" ? "En línea" : "Online"}
				</span>
			);
		}
		return null;
	};

	return (
		<div className="sf-team-services-overlay" onClick={onClose}>
			<div className="sf-team-services-modal" onClick={(e) => e.stopPropagation()}>
				{/* HEADER */}
				<div className="sf-team-services-header">
					<div className="sf-team-services-header-text">
						<h2 className="sf-team-services-title">
							{language === "es"
								? `Servicios de ${staffProfile.fullName}`
								: `Manage Services for ${staffProfile.fullName}`}
						</h2>
						<span className="sf-team-services-subtitle">
							{language === "es"
								? `${selectedCount} de ${rows.length} seleccionados`
								: `${selectedCount} of ${rows.length} selected`}
						</span>
					</div>
					<button className="sf-team-services-close" onClick={onClose} aria-label="Close">✕</button>
				</div>

				{/* BODY */}
				<div className="sf-team-services-body">
					{rows.length === 0 ? (
						<div className="sf-team-services-empty">
							{language === "es" ? "No hay servicios disponibles" : "No services available"}
						</div>
					) : (
						rows.map((row, idx) => (
							<button
								key={row.serviceId}
								type="button"
								className={`sf-tsm-card ${row.active ? "is-active" : ""}`}
								onClick={() => toggleRow(idx)}
							>
								<span className="sf-tsm-check" aria-hidden="true">
									{row.active && <Check size={14} strokeWidth={2.6} />}
								</span>

								<div className="sf-tsm-info">
									<div className="sf-tsm-name">{row.name}</div>
									<div className="sf-tsm-meta">
										{locationBadge(row)}
									</div>
								</div>

								<div className="sf-tsm-pricing">
									<span className="sf-tsm-price">{row.currency || "USD"} {row.price ?? 0}</span>
									<span className="sf-tsm-duration">{row.duration} min</span>
								</div>
							</button>
						))
					)}
				</div>

				{/* FOOTER */}
				<div className="sf-base-footer">
					<button className="btn ghost" onClick={onClose}>
						{t.calendar.editor.cancel}
					</button>
					<button className="btn" onClick={handleSave} disabled={saving}>
						{saving ? t.calendar.editor.saving : t.calendar.editor.save}
					</button>
				</div>
			</div>
		</div>
	);
};

export default TeamServicesModal;